import { useEffect, useState } from "react";
import { listClassSessions, materializeClassSessions, updateClassSession } from "../lib/api/endpoints";
import type { ClassDto, ClassSessionDto, ClassSessionStatus } from "../types";
import { Button } from "./Button";
import { C } from "../theme";
import { Snackbar } from "./Snackbar";

const statusLabels: Record<ClassSessionStatus, string> = { 1: "Agendada", 2: "Cancelada", 3: "Concluída", 4: "Bloqueada" };

function isoDate(value: Date) {
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
}

function sessionsErrorMessage(cause: unknown, fallback: string) {
  if (cause instanceof TypeError && cause.message === "Failed to fetch") return "Não foi possível conectar à API. Verifique se o servidor está ativo e tente novamente.";
  return cause instanceof Error && cause.message ? cause.message : fallback;
}

export function ClassSessionsPanel({ token, item }: { token: string; item: ClassDto }) {
  const [sessions, setSessions] = useState<ClassSessionDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const start = new Date();
      const end = new Date(start);
      end.setDate(end.getDate() + 30);
      const result = await listClassSessions(token, { startDate: isoDate(start), endDate: isoDate(end), courtId: item.courtId });
      setSessions(result.sessions.filter(session => session.classId === item.id));
    } catch (cause) {
      setError(sessionsErrorMessage(cause, "Não foi possível carregar as sessões."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [token, item.id, item.courtId]);

  const materialize = async () => {
    setLoading(true);
    setError("");
    try { await materializeClassSessions(token); await load(); } catch (cause) { setError(sessionsErrorMessage(cause, "Não foi possível gerar as sessões.")); } finally { setLoading(false); }
  };

  const changeStatus = async (session: ClassSessionDto, status: ClassSessionStatus) => {
    setError("");
    const reasons: Record<ClassSessionStatus, string> = { 1: "Reagendada pelo gestor", 2: "Cancelada pelo gestor", 3: "Concluída pelo gestor", 4: "Bloqueada pelo gestor" };
    try { await updateClassSession(token, session.id, { status, reason: reasons[status] }); await load(); } catch (cause) { setError(sessionsErrorMessage(cause, "Não foi possível atualizar a sessão.")); }
  };

  return <div style={{ display: "grid", gap: 8, borderTop: `1px solid ${C.faint}`, paddingTop: 8 }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}><span style={{ color: C.light, fontSize: 11 }}>Próximas ocorrências (30 dias)</span><div style={{ display: "flex", gap: 6 }}><Button variant="ghost" onClick={() => void materialize()} disabled={loading}>Gerar próximas ocorrências</Button><Button variant="ghost" onClick={() => void load()} disabled={loading}>Atualizar</Button></div></div>
    {error && <Snackbar message={error} onClose={() => setError("")} />}
    {!loading && sessions.length === 0 && <span style={{ color: C.light, fontSize: 11 }}>Nenhuma sessão materializada para este período.</span>}
    {sessions.map(session => <div key={session.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, color: C.gray, fontSize: 11 }}><span>{new Date(`${session.date}T12:00:00`).toLocaleDateString("pt-BR")} · {session.startTime.slice(11, 16)} — {session.endTime.slice(11, 16)} · {statusLabels[session.status]}</span>{session.status === 1 && <div style={{ display: "flex", gap: 4 }}><Button variant="ghost" onClick={() => void changeStatus(session, 2)}>Cancelar</Button><Button variant="ghost" onClick={() => void changeStatus(session, 4)}>Bloquear</Button><Button variant="ghost" onClick={() => void changeStatus(session, 3)}>Concluir</Button></div>}</div>)}
  </div>;
}
